import React, { useState, FormEvent } from "react";

import classNames from "classnames";
import Link from "next/link";

import { ProductData } from "data/product/products";
import { StorefrontData } from "data/storefront";
import { theme } from "studio-design-system";

import { ApiStatus } from "~/api/fetchStudioApi";
import { CreatorCheck } from "~/components/common/CreatorInfo/CreatorCheck";
import { ProfilePic } from "~/components/common/ProfilePic";
import * as productStyles from "~/components/videoProductPage/Product.css";
import { useCreatorEditing, CreatorSavingType, errorBorderStyle } from "~/hooks/useCreatorEditing";

import * as baseStyles from "../VideoSection.css";
import * as mobileStyles from "./TaughtBySection.css";

export const TaughtBySection = ({
  product,
  storefrontData,
  isInEditMode,
  topicTitleColor = theme.colors.content.secondary,
  creatorType = "",
}: {
  product: ProductData;
  storefrontData: StorefrontData;
  isInEditMode: boolean;
  topicTitleColor?: string;
  creatorType?: string;
}): React.ReactElement => {
  const { fieldsTexts, fieldsOnError, handleTextChange, handleTextEdit } = useCreatorEditing();

  const creatorName = isInEditMode ? fieldsTexts["creatorName"] : storefrontData.creatorName;

  return (
    <div
      className={classNames(
        mobileStyles.container,
        baseStyles.productContainer,
        baseStyles.sectionMargin,
      )}
    >
      <p
        className={baseStyles.topicsTitle}
        style={{
          color: topicTitleColor,
        }}
      >
        Taught by
      </p>
      <div className={classNames(mobileStyles.innerContainer, baseStyles.authorInfo)}>
        <div className={classNames(mobileStyles.hideOnMobile, baseStyles.authorImgBlock)}>
          <div className={baseStyles.authorImageContainer}>
            <ProfilePic
              className={baseStyles.authorImg}
              size={80}
              borderRadius={"12px"}
              imageUrl={storefrontData.welcomePosterImageURL}
              firstName={creatorName}
            />
          </div>
        </div>
        <div className={classNames(mobileStyles.mobileAuthor, baseStyles.authorTitleShort)}>
          <div className={baseStyles.authorTitle}>
            <div className={classNames(mobileStyles.authorImg, baseStyles.authorImageContainer)}>
              <ProfilePic
                className={baseStyles.authorImg}
                size={26}
                borderRadius={"50%"}
                imageUrl={storefrontData.welcomePosterImageURL}
                firstName={creatorName}
              />
            </div>
            {!isInEditMode ? (
              <Link passHref href={`/classes/${storefrontData.slug}`}>
                <a className={baseStyles.titleText}>{creatorName}</a>
              </Link>
            ) : (
              <div
                className={classNames(
                  isInEditMode ? baseStyles.titleTextEditable : baseStyles.titleText,
                  isInEditMode && productStyles.editableElement,
                )}
                contentEditable={isInEditMode}
                suppressContentEditableWarning={true}
                onBlur={(e) => handleTextChange(e, "creatorName", CreatorSavingType.STOREFRONT)}
                onInput={(e) => handleTextEdit(e, "creatorName", CreatorSavingType.STOREFRONT)}
                style={fieldsOnError.includes("creatorName") ? errorBorderStyle : undefined}
              >
                {creatorName}
              </div>
            )}
            {creatorType ? (
              <span className={baseStyles.creatorType}>{creatorType}</span>
            ) : (
              <CreatorCheck />
            )}
          </div>
          <div
            className={classNames(
              mobileStyles.description,
              baseStyles.authorShortDes,
              isInEditMode && productStyles.editableElement,
            )}
            contentEditable={isInEditMode}
            suppressContentEditableWarning={true}
            onBlur={(e) => handleTextChange(e, "shortBio", CreatorSavingType.STOREFRONT)}
            onInput={(e) => handleTextEdit(e, "shortBio", CreatorSavingType.STOREFRONT)}
            style={fieldsOnError.includes("shortBio") ? errorBorderStyle : undefined}
          >
            {isInEditMode ? fieldsTexts["shortBio"] : storefrontData.shortBio}
          </div>
        </div>
      </div>
    </div>
  );
};
